import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/queryKeys'
import {
  listBookings,
  cancelBooking,
  markNoShow,
  rescheduleBooking,
  exportBookingsCsv,
  getCalendarAnalytics,
} from '../api'
import { BOOKING_STATUS } from '../constants/calendarConstants'

export function useBookings(workspaceId, filters = {}) {
  return useQuery({
    queryKey: queryKeys.calendarBookings(workspaceId, filters),
    queryFn: () => listBookings(workspaceId, filters),
    enabled: !!workspaceId,
    staleTime: 60 * 1000,
  })
}

export function useUpcomingBookingCount(workspaceId) {
  const { data: count = 0 } = useQuery({
    queryKey: queryKeys.calendarBookings(workspaceId),
    queryFn: () => listBookings(workspaceId),
    enabled: !!workspaceId,
    staleTime: 10 * 1000,
    refetchInterval: 15 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    select: (bookings) => {
      const now = Date.now()
      return bookings.filter(
        (booking) =>
          booking.status === BOOKING_STATUS.CONFIRMED &&
          new Date(booking.startsAt).getTime() >= now
      ).length
    },
  })
  return count
}

export function useCalendarAnalytics(workspaceId) {
  return useQuery({
    queryKey: queryKeys.calendarAnalytics(workspaceId),
    queryFn: () => getCalendarAnalytics(workspaceId),
    enabled: !!workspaceId,
    staleTime: 60 * 1000,
  })
}

export function useCancelBooking(workspaceId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }) => cancelBooking(workspaceId, id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.calendarBookings(workspaceId) })
      qc.invalidateQueries({ queryKey: queryKeys.calendarAnalytics(workspaceId) })
    },
  })
}

export function useMarkNoShow(workspaceId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => markNoShow(workspaceId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.calendarBookings(workspaceId) })
      qc.invalidateQueries({ queryKey: queryKeys.calendarAnalytics(workspaceId) })
    },
  })
}

export function useRescheduleBooking(workspaceId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, startsAt, endsAt }) =>
      rescheduleBooking(workspaceId, id, { startsAt, endsAt }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.calendarBookings(workspaceId) })
      qc.invalidateQueries({ queryKey: queryKeys.calendarAnalytics(workspaceId) })
    },
  })
}

export function useExportBookings(workspaceId, filters = {}) {
  return useMutation({
    mutationFn: () => exportBookingsCsv(workspaceId, filters),
  })
}
